import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// BMW M3 (E30, 1986) — the homologation-special sports coupe at true scale
// (1 unit = 1 metre): length 4.36, width 1.68 over the flared arches, height
// 1.37, wheelbase 2.56, on ~15-inch BBS-style alloys. Nose is +Z. A crisp,
// boxy, folded-paper three-box two-door saloon: flat bonnet, an upright glassy
// cabin whose glass is flush under a body-width roof, and a short flat boot
// stepped down from the roof with a small ducktail lip and a fast, raked rear
// window. The M3 signature is a set of squared black arch flares — kept to a
// thin lip proud of the body so the tyres sit just inside them, not swallowed.
// Front cues: the BMW twin-kidney grille flanked by quad round headlamps in a
// black nose panel, a slim body-colour bumper and a chin air dam. Rear: wide
// three-section tail lamps with a body-colour panel between them, twin exhaust
// tips. Body panels carry the "paint" recolour slot (iconic Alpine White);
// `drive` spins the wheels and `hazards` blinks the six indicators.

const PAINT = "#eef0f0"; // iconic Alpine White — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, nose panel, window frames)
const CLAD = "#26282c"; // matte black plastic (arch lips, chin, valance, sills)
const WELL = "#141518"; // dark wheel wells inside the arch lips
const GLASS = "#2a3742"; // tinted side / rear glazing
const SCREEN = "#3a4c5e"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SILVER = "#c4c9cf"; // BBS-style alloy spokes, kidney slats
const HUB = "#6b7076"; // lighter grey alloy hub face behind the spokes
const RIMDARK = "#3c3f44"; // rim barrel
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527"; // tail lamps
const REDDARK = "#701020";
const PLATE = "#eceff2";
const SLAT = "#3a3e44"; // grille kidney inner darkness
const DARK = "#101113"; // headlamp buckets

// Spec-sheet skeleton (metres). Modest track so the tyres sit just inside the
// thin arch lips (not proud fender tents), for a planted low-saloon stance.
const WHEEL_R = 0.31;
const WHEEL_X = 0.71; // half track (1.42)
const AXLE_F = 1.28;
const AXLE_R = -1.28; // wheelbase 2.56
const BODY_HW = 0.79; // body-tub half-width (1.58); arch lips flare to ~0.85

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One ~15-inch BBS-style alloy: tyre, brake disc, dark rim barrel, a lighter
 *  grey hub face, five bright cross-spokes and a chrome centre cap. Every part
 *  shares `name` so the `drive` track spins them as one node about the axle.
 *  `side` is +1 for the right (+x) wheel, -1 for the left. Outer tyre face at
 *  0.83, just inside the arch lip (0.85). */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 0.24, 18, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.14, 0.14, 0.03, 16, BRAKE, [x - side * 0.06, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.235, 0.235, 0.06, 18, RIMDARK, [x + side * 0.05, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.205, 0.205, 0.03, 18, HUB, [x + side * 0.085, y, z], [0, 0, Math.PI / 2], { name }),
  ];
  for (let k = 0; k < 5; k++) {
    parts.push(
      box(0.06, 0.39, 0.05, SILVER, [x + side * 0.09, y, z], [(k * 2 * Math.PI) / 5, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.06, 0.06, 0.03, 14, CHROME, [x + side * 0.1, y, z], [0, 0, Math.PI / 2], { name }));
  return parts;
}

/** One squared arch flare + its dark wheel well. The M3 flare is kept subtle:
 *  a thin black lip (a top brow plus a short front and rear leg) standing only
 *  ~0.05 proud of the white body side, tracing the wheel opening as a squared
 *  arch. The dark well disc hugs the tyre (radius just over the tyre) so the
 *  arch gap is closed, and the tyre's outer face is proud of the disc so the
 *  alloy fills the arch. Black lips on Alpine White are the iconic look. */
function flare(side: 1 | -1, z: number): Part[] {
  const lx = side * 0.82; // lip outer face ~0.85 → arch width ~1.70 (~spec 1.68)
  return [
    cyl(0.37, 0.37, 0.03, 20, WELL, [side * 0.795, WHEEL_R, z], [0, 0, Math.PI / 2]),
    box(0.06, 0.07, 0.74, CLAD, [lx, 0.65, z]), // thin top brow, just above the tyre
    box(0.06, 0.34, 0.07, CLAD, [lx, 0.47, z + 0.35]), // short front leg
    box(0.06, 0.34, 0.07, CLAD, [lx, 0.47, z - 0.35]), // short rear leg
  ];
}

// The four hazard indicators (a classic car needs no more) — parts and blink
// tracks are generated from one table so the pivots (each part's own centre)
// can never drift from the parts. Front pair sits in the black nose panel; rear
// pair is the amber inner segment of each tail-lamp cluster.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.62, 0.6, 2.055], size: [0.12, 0.07, 0.03] },
  { name: "indicator-fr", pos: [0.62, 0.6, 2.055], size: [0.12, 0.07, 0.03] },
  { name: "indicator-rl", pos: [-0.28, 0.72, -2.045], size: [0.16, 0.17, 0.05] },
  { name: "indicator-rr", pos: [0.28, 0.72, -2.045], size: [0.16, 0.17, 0.05] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...flare(s, z));
  }
  P.push(box(1.5, 0.16, 3.7, TRIM, [0, 0.22, 0])); // low underbody pan, closes the profile

  // -- Body masses (paint): the folded-paper three-box, dropped onto the wheels
  P.push(paint(BODY_HW * 2, 0.62, 3.72, [0, 0.61, -0.02])); // main tub, low sill (0.30) to shoulder (0.92)
  P.push(paint(BODY_HW * 2, 0.07, 3.3, [0, 0.92, -0.1])); // belt-line shoulder band
  P.push(paint(1.44, 0.08, 1.05, [0, 0.9, 1.42], [0.05, 0, 0])); // flat bonnet
  for (const s of S) P.push(paint(0.09, 0.09, 1.05, [s * 0.735, 0.915, 1.42], [0.05, 0, 0])); // fender tops
  P.push(paint(BODY_HW * 2, 0.42, 0.16, [0, 0.68, 1.95])); // nose block behind the fascia
  P.push(paint(BODY_HW * 2, 0.42, 0.16, [0, 0.68, -1.95])); // tail block behind the fascia
  // Low flat closed trunk: a solid block whose flat top lid sits at ~bonnet
  // height (0.95), well below the roof, with NOTHING rising beside it — so the
  // rear is a sealed third box, no walls and no cavity to look down into.
  P.push(paint(BODY_HW * 2, 0.3, 0.52, [0, 0.8, -1.71])); // solid trunk, flat lid top at 0.95
  P.push(paint(1.5, 0.05, 0.1, [0, 0.965, -1.94])); // ducktail lip along the trunk's trailing edge

  // -- Black plastic: sills, slim bumpers, chin air dam, valance --------------
  for (const s of S) P.push(box(0.05, 0.14, 2.5, CLAD, [s * 0.78, 0.32, -0.1])); // sill, arch to arch
  P.push(paint(1.5, 0.13, 0.12, [0, 0.5, 2.02])); // front bumper (body colour)
  P.push(box(1.5, 0.05, 0.06, CLAD, [0, 0.5, 2.09])); // front bumper rubber insert
  P.push(paint(1.5, 0.13, 0.12, [0, 0.5, -2.02])); // rear bumper (body colour)
  P.push(box(1.5, 0.05, 0.06, CLAD, [0, 0.5, -2.09])); // rear bumper rubber insert
  P.push(box(1.5, 0.15, 0.1, CLAD, [0, 0.36, 2.0])); // front chin air dam
  P.push(box(1.3, 0.09, 0.05, CLAD, [0, 0.31, 2.03])); // chin lower lip
  P.push(box(1.5, 0.15, 0.1, CLAD, [0, 0.36, -2.0])); // rear valance
  P.push(box(1.46, 0.2, 0.3, TRIM, [0, 0.4, 1.82])); // under-nose fill (no see-through)
  P.push(box(1.46, 0.2, 0.3, TRIM, [0, 0.4, -1.82])); // under-tail fill

  // -- Greenhouse: a sealed, upright glasshouse — glass flush under the roof --
  // Roof half-width (0.75) sits just inside the body (0.79); the side glass is
  // vertical and flush under the roof edge, so roof + glass + body read as one
  // crisp box with no panel winging past the roofline.
  P.push(box(1.46, 0.04, 0.14, TRIM, [0, 0.94, 0.82])); // cowl at the screen base
  P.push(box(1.44, 0.32, 0.04, SCREEN, [0, 1.09, 0.71], [-0.62, 0, 0])); // upright windscreen
  for (const s of S) P.push(paint(0.08, 0.34, 0.08, [s * 0.7, 1.09, 0.71], [-0.62, 0, 0])); // A-pillars
  // Flat roof, body-width, set far back and lengthened so the cabin dominates
  // and only a short boot sits behind it. Shallow greenhouse: roof top ~1.26, a
  // low glasshouse over a high beltline so it reads as a low saloon, not a cab.
  P.push(paint(1.5, 0.06, 1.78, [0, 1.24, -0.38]));
  for (const s of S) {
    // Glass tucked just inboard of the roof edge (0.73 < roof 0.75) so nothing
    // wings past the roofline in the front/back views. Shallow (belt 1.0 to 1.22).
    P.push(box(0.04, 0.24, 1.74, GLASS, [s * 0.73, 1.11, -0.38])); // side glass, vertical + sealed
    P.push(box(0.05, 0.24, 0.06, TRIM, [s * 0.738, 1.11, -0.24])); // B-pillar
    P.push(paint(0.07, 0.25, 0.16, [s * 0.735, 1.11, -1.07])); // C-pillar quarter (slim)
    P.push(paint(0.06, 0.1, 0.14, [s * 0.742, 1.02, -0.99])); // Hofmeister kink hook
    P.push(box(0.02, 0.03, 1.9, CHROME, [s * 0.795, 0.985, -0.3])); // belt-line chrome strip
  }
  // Solid rear quarters (sail panels): fill the whole triangle from the roof
  // rear down to the trunk on each side as SOLID body, so there are no
  // free-standing pillars and no open cavity — a backlight window is recessed
  // into that solid mass. This is what makes the rear read as a sealed box.
  for (const s of S) P.push(paint(0.14, 0.32, 0.6, [s * 0.72, 1.05, -1.42], [0.34, 0, 0])); // solid C-pillar sail
  // Backlight window recessed between the sails, sloping roof -> trunk lid.
  P.push(box(1.28, 0.34, 0.03, GLASS, [0, 1.09, -1.4], [0.78, 0, 0]));
  P.push(box(0.44, 0.025, 0.02, REDDARK, [0, 0.955, -1.62])); // thin third-brake strip at the trunk lid

  // -- Front fascia: twin-kidney grille + quad round lamps in a black panel ---
  P.push(box(1.42, 0.32, 0.04, TRIM, [0, 0.72, 2.03])); // full-width black nose panel
  for (const s of S) {
    // Kidney: bright surround, dark inset, three vertical slats.
    const kx = s * 0.13;
    P.push(box(0.2, 0.27, 0.04, SILVER, [kx, 0.75, 2.05])); // kidney surround
    P.push(box(0.16, 0.23, 0.03, SLAT, [kx, 0.75, 2.065])); // kidney darkness
    for (const dx of [-0.05, 0, 0.05]) {
      P.push(box(0.015, 0.21, 0.02, SILVER, [kx + dx, 0.75, 2.075])); // vertical slats
    }
    // Quad round headlamps: a larger outer and a smaller inner lamp per side.
    P.push(cyl(0.14, 0.14, 0.02, 18, DARK, [s * 0.58, 0.75, 2.04], [Math.PI / 2, 0, 0])); // outer bucket
    P.push(cyl(0.14, 0.14, 0.012, 18, CHROME, [s * 0.58, 0.75, 2.05], [Math.PI / 2, 0, 0])); // outer ring
    P.push(cyl(0.115, 0.115, 0.02, 18, LAMP, [s * 0.58, 0.75, 2.07], [Math.PI / 2, 0, 0])); // outer lens
    P.push(cyl(0.095, 0.095, 0.02, 18, DARK, [s * 0.35, 0.75, 2.05], [Math.PI / 2, 0, 0])); // inner bucket
    P.push(cyl(0.095, 0.095, 0.012, 18, CHROME, [s * 0.35, 0.75, 2.06], [Math.PI / 2, 0, 0])); // inner ring
    P.push(cyl(0.075, 0.075, 0.02, 18, LAMP, [s * 0.35, 0.75, 2.075], [Math.PI / 2, 0, 0])); // inner lens
  }
  P.push(box(0.44, 0.11, 0.02, PLATE, [0, 0.5, 2.1])); // front plate on the bumper

  // -- Rear fascia: three-section tail lamps + body-colour centre panel -------
  for (const s of S) {
    // Each side: a wide horizontal cluster — amber inner (the indicator, drawn
    // from the table), a red brake block and a red outer, on a dark backing.
    P.push(box(0.56, 0.19, 0.04, TRIM, [s * 0.44, 0.72, -2.02])); // cluster backing
    P.push(box(0.16, 0.17, 0.05, RED, [s * 0.46, 0.72, -2.045])); // red brake block
    P.push(box(0.16, 0.17, 0.05, RED, [s * 0.62, 0.72, -2.045])); // red outer block
    P.push(box(0.03, 0.05, 0.02, REDDARK, [s * 0.68, 0.55, -2.05])); // low reflector
  }
  P.push(paint(0.34, 0.19, 0.06, [0, 0.72, -2.02])); // body-colour panel between the lamps
  P.push(cyl(0.05, 0.05, 0.02, 16, CHROME, [0, 0.72, -2.06], [Math.PI / 2, 0, 0])); // rear badge
  P.push(box(0.44, 0.11, 0.02, PLATE, [0, 0.5, -2.1])); // rear plate on the bumper
  for (const dx of [-0.3, -0.44]) {
    P.push(cyl(0.045, 0.045, 0.1, 12, CHROME, [dx, 0.29, -2.05], [Math.PI / 2, 0, 0])); // twin exhaust tips
  }

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.04, 0.025, 0.03, TRIM, [s * 0.8, 0.95, 0.72])); // mirror arm
    P.push(paint(0.08, 0.075, 0.08, [s * 0.83, 0.96, 0.7])); // mirror cap
    P.push(box(0.035, 0.055, 0.012, CHROME, [s * 0.83, 0.96, 0.66])); // mirror glass
    P.push(box(0.018, 0.024, 0.13, CHROME, [s * 0.79, 0.75, -0.1])); // door handle
    P.push(box(0.34, 0.012, 0.025, TRIM, [s * 0.26, 0.98, 0.78], [-0.56, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const bmwM3E30 = defineModel({
  id: "bmw-m3-e30",
  name: "BMW M3 (E30)",
  batchId: "cars",
  tags: ["car", "bmw", "m3", "e30", "coupe", "sports", "classic"],
  summary: "A 1986 BMW M3 (E30) sports coupe at true scale, in Alpine White.",
  description:
    "A BMW M3 (E30, 1986) at real 1 unit = 1 metre scale (about 4.36 long, " +
    "1.68 wide over the arches, 1.37 tall, 2.56 wheelbase, on BBS-style " +
    "alloys). A crisp, boxy, folded-paper three-box two-door saloon: flat " +
    "bonnet, an upright glassy cabin whose glass is flush under a body-width " +
    "roof, and a short flat boot stepped down from the roof with a ducktail " +
    "lip and a fast, raked rear window. The M3 signature is a set of squared " +
    "black arch flares kept to a thin lip proud of the body, the tyres sitting " +
    "just inside them. Front: the BMW twin-kidney grille flanked by quad round " +
    "headlamps in a black nose panel, a slim body-colour bumper, a chin air " +
    "dam. Rear: three-section tail lamps with a body-colour panel between " +
    "them, twin exhaust tips. Body panels carry the 'paint' recolour slot " +
    "(iconic Alpine White); `drive` spins the wheels and `hazards` blinks the " +
    "six indicators.",
  author: "Fable 5",
  footprint: 2.33,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
